/**
 * Promotional modal encouraging merchants on the old Manage Returns page to try the redesign.
 * Shown once per logged-in user until they dismiss it or opt into the redesign.
 */
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import FontHeading2 from "../../../../components/fonts/font-heading2/font-heading2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../components/fonts/font-props";
import IconCheck from "../../../../components/icons/icon-check/icon-check";
import IconDiscard from "../../../../components/icons/icon-discard/icon-discard";
import Icon from "../../../../components/icons/icon/icon";
import { Button } from "../../../../components/inputs/button/button";
import { ButtonIcon } from "../../../../components/inputs/button-icon/button-icon";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import { Modal } from "../../../../components/modal/modal";
import useDashboardTranslation from "../../../../dashboard-translation/hooks/dashboard-translation-hook";
import dashboardTranslationsStore from "../../../../dashboard-translation/stores/dashboard-translations-store";
import rmsMixpanelStore from "../../../../stores/rms-mixpanel-store";
import uiSettingsStore from "../../../../stores/ui-settings-store";
import { buildManageReturnsDesignPath } from "../../utils/manage-returns-redesign-rollout";
import ManageReturnsRedesignPromoDecorations from "./manage-returns-redesign-promo-decorations";
import "./manage-returns-redesign-promo-modal.scss";

const TRANSLATION_SCOPE = "dashboard-returns-page";

const BULLET_KEYS = [
    "MANAGE_RETURNS_REDESIGN_PROMO_BULLET_1",
    "MANAGE_RETURNS_REDESIGN_PROMO_BULLET_2",
    "MANAGE_RETURNS_REDESIGN_PROMO_BULLET_3",
] as const;

const BULLET_FALLBACKS = [
    "Action-oriented — see what needs you first",
    "Easier filters to find the right RMAs fast",
    "RMAs organized in a clearer hierarchy",
] as const;

const IconDiscardWhite = () => <IconDiscard color="var(--color-neutral-1-white)" />;

const ManageReturnsRedesignPromoModal: React.FC = observer(() => {
    const { getTranslation } = useDashboardTranslation();
    const navigate = useNavigate();
    const isSaving = uiSettingsStore.isSaving();

    const getText = (key: string, fallback: string) => {
        const translated = getTranslation(key, TRANSLATION_SCOPE);
        return translated === `${TRANSLATION_SCOPE}.${key}` ? fallback : translated;
    };

    const onNotNow = async () => {
        if (isSaving) {
            return;
        }

        rmsMixpanelStore.trackEvent("d&_manage_returns_redesign_promo_dismiss");
        await uiSettingsStore.saveManageReturnsPromoDeclined();
    };

    const onTryItNow = async () => {
        if (isSaving) {
            return;
        }

        rmsMixpanelStore.trackEvent("d&_manage_returns_redesign_promo_try_it");
        if (await uiSettingsStore.saveManageReturnsNewEnabled(true)) {
            navigate(buildManageReturnsDesignPath(true));
        }
    };

    const bullets = BULLET_KEYS.map((key, index) => getText(key, BULLET_FALLBACKS[index]));

    return (
        <Modal onCloseClicked={onNotNow}>
            <div
                className="manage-returns-redesign-promo-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="manage-returns-redesign-promo-title"
                tabIndex={-1}
            >
                {!dashboardTranslationsStore.isLoading && (
                    <>
                        <div className="manage-returns-redesign-promo-modal__header">
                            <ManageReturnsRedesignPromoDecorations />
                            <span className="manage-returns-redesign-promo-modal__badge">
                                {getText("MANAGE_RETURNS_REDESIGN_PROMO_BADGE", "Just launched")}
                            </span>
                            <div className="manage-returns-redesign-promo-modal__close">
                                <ButtonIcon Component={IconDiscardWhite} onClick={onNotNow} disabled={isSaving} />
                            </div>
                            <div
                                id="manage-returns-redesign-promo-title"
                                className="manage-returns-redesign-promo-modal__title"
                            >
                                <FontHeading2 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}>
                                    {getText(
                                        "MANAGE_RETURNS_REDESIGN_PROMO_TITLE",
                                        "Managing returns just got easier"
                                    )}
                                </FontHeading2>
                            </div>
                        </div>
                        <div className="manage-returns-redesign-promo-modal__body">
                            <div className="manage-returns-redesign-promo-modal__description">
                                <FontBody1 color={FONT_COLOR.PRIMARY3}>
                                    {getText(
                                        "MANAGE_RETURNS_REDESIGN_PROMO_DESCRIPTION",
                                        "Built to help you clear your queue faster — switch on anytime, switch back anytime."
                                    )}
                                </FontBody1>
                            </div>
                            <ul className="manage-returns-redesign-promo-modal__bullets">
                                {bullets.map((bullet, index) => (
                                    <li key={BULLET_KEYS[index]} className="manage-returns-redesign-promo-modal__bullet">
                                        <span className="manage-returns-redesign-promo-modal__check">
                                            <Icon Component={IconCheck} color="var(--color-neutral-1-white)" />
                                        </span>
                                        <FontBody1 color={FONT_COLOR.NEUTRAL11_MENU}>{bullet}</FontBody1>
                                    </li>
                                ))}
                            </ul>
                            <div className="manage-returns-redesign-promo-modal__actions">
                                <Button
                                    text={getText("MANAGE_RETURNS_REDESIGN_PROMO_NOT_NOW", "Not now")}
                                    variant={BUTTON_VARIANTS.Secondary}
                                    onClick={onNotNow}
                                    disabled={isSaving}
                                />
                                <Button
                                    text={getText("MANAGE_RETURNS_REDESIGN_PROMO_TRY_IT", "Try it now")}
                                    variant={BUTTON_VARIANTS.Primary}
                                    onClick={onTryItNow}
                                    disabled={isSaving}
                                    hasLoader
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
});

export default ManageReturnsRedesignPromoModal;
